import React from 'react';
import Alert from '@amzn/awsui-components-core/alert';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import FormField from '@amzn/awsui-components-core/form-field';
import Modal from '@amzn/awsui-components-core/modal';
import Multiselect from '@amzn/awsui-components-core/multiselect';
import Select from '@amzn/awsui-components-core/select';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import type { DatasetWizardState } from 'src/components/star-grid/datasets/hooks/useDatasetWizard';
import { useLookupDraft } from 'src/components/star-grid/datasets/hooks/useLookupDraft';
import { resultChecks } from 'src/components/star-grid/datasets/utils/lookupChecks';
import type { DatasetLookup } from 'src/components/star-grid/datasets/datasetTypes';
import { LookupChecks } from 'src/components/star-grid/datasets/components/LookupChecks';
import styles from 'src/components/star-grid/datasets/components/styles/DatasetWizard.module.scss';

interface Props {
  readonly state: DatasetWizardState;
  readonly text: StarGridText;
  /** The dimension column the lookup enriches; fixed, because the editor is opened from its row. */
  readonly column: string;
  /** An accepted lookup to change, prefilled into the form. */
  readonly existing?: DatasetLookup;
  readonly onDismiss: () => void;
}

/** Declares one lookup join for a column with no Prophecy hierarchy, checked (unique key, match, fan-out) before it is added. */
export const LookupEditorModal: React.FC<Props> = ({ state, text, column, existing, onDismiss }) => {
  const draft = useLookupDraft(state.selectedRef ?? '', state.appliedFixedFilters, state.roleArn);
  const { setColumn, prefill } = draft;
  React.useEffect(() => {
    if (existing) prefill(existing);
    else setColumn(column);
  }, [column, existing, prefill, setColumn]);

  const tableOptions = state.sources.map((source) => ({ value: source.ref, label: source.ref }));
  const keyOptions = draft.columns.map((entry) => ({ value: entry.name, label: `${entry.name} (${entry.type})` }));
  const attributeOptions = draft.columns.filter((entry) => entry.name !== draft.key).map((entry) => ({ value: entry.name, label: entry.name }));

  const add = (): void => {
    const lookup = draft.build();
    if (lookup && draft.check) {
      state.upsertLookup(lookup, resultChecks(draft.check));
      onDismiss();
    }
  };

  return (
    <Modal
      visible
      size="medium"
      header={`${text('wizardLookupAdd')}: ${state.labels[column]?.trim() || column}`}
      onDismiss={onDismiss}
      data-testid="lookup-editor"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onDismiss}>{text('wizardCancel')}</Button>
            <Button data-testid="lookup-check" disabled={!draft.canCheck} loading={draft.checking} onClick={draft.runCheck}>
              {text('wizardLookupRunCheck')}
            </Button>
            <Button variant="primary" data-testid="lookup-add" disabled={!draft.canAdd} onClick={add}>
              {text('wizardLookupAddButton')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="s">
        <p className={styles.help}>{text('wizardLookupsHelp')}</p>
        <div className={styles.lookupForm}>
          <FormField label={text('wizardLookupTable')}>
            <Select
              ariaLabel={text('wizardLookupTable')}
              data-testid="lookup-table"
              expandToViewport
              filteringType="auto"
              options={tableOptions}
              selectedOption={tableOptions.find((option) => option.value === draft.tableRef) ?? null}
              onChange={({ detail }) => draft.setTableRef(detail.selectedOption.value ?? '')}
            />
          </FormField>
          <FormField label={text('wizardLookupKey')} errorText={draft.columnsError}>
            <Select
              ariaLabel={text('wizardLookupKey')}
              data-testid="lookup-key"
              expandToViewport
              disabled={!draft.tableRef}
              statusType={draft.columnsLoading ? 'loading' : draft.columnsError ? 'error' : 'finished'}
              options={keyOptions}
              selectedOption={keyOptions.find((option) => option.value === draft.key) ?? null}
              onChange={({ detail }) => draft.setKey(detail.selectedOption.value ?? '')}
            />
          </FormField>
          <FormField label={text('wizardLookupAttributes')}>
            <Multiselect
              ariaLabel={text('wizardLookupAttributes')}
              data-testid="lookup-attributes"
              expandToViewport
              disabled={!draft.key}
              options={attributeOptions}
              selectedOptions={attributeOptions.filter((option) => draft.attributes.includes(option.value))}
              onChange={({ detail }) => draft.setAttributes(detail.selectedOptions.map((option) => option.value ?? ''))}
            />
          </FormField>
        </div>
        {draft.check && (
          <Box data-testid="lookup-check-result">
            <LookupChecks text={text} checks={resultChecks(draft.check)} />
          </Box>
        )}
        {draft.checkError && (
          <Alert type="error" data-testid="lookup-check-error">
            {draft.checkError}
          </Alert>
        )}
      </SpaceBetween>
    </Modal>
  );
};
